import Reveal from '../components/Reveal';
import { useLanguage } from '../context/LanguageContext';
import { getYouTubeEmbedUrl } from '../utils/getYouTubeEmbedUrl';

function FounderPage() {
  const { t } = useLanguage();
  const f = t.founder;
  const m = f.media;
  const intro = f.introVideo;
  const youtubeEmbedSrc = getYouTubeEmbedUrl(m.videoSrc);
  const introEmbedSrc = getYouTubeEmbedUrl(intro?.videoSrc);
  const visionIcons = ['hub', 'real_estate_agent', 'campaign'];

  return (
    <main className="inner-page founder-page">
      <section className="founder-page-intro">
        <div className="container founder-page-intro-inner">
          <Reveal direction="left">
            <div className="founder-page-intro-copy">
              <span className="eyebrow founder-page-hero-eyebrow">{intro.eyebrow}</span>
              <h2 className="founder-page-intro-title">{intro.title}</h2>
              <p className="founder-page-intro-text">{intro.text}</p>
              {intro.attribution ? (
                <p className="founder-page-intro-attribution">{intro.attribution}</p>
              ) : null}
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="founder-page-intro-video">
              <div className="founder-page-intro-video-frame">
                {introEmbedSrc ? (
                  <iframe
                    title={intro.title}
                    src={`${introEmbedSrc}?rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : (
                  <div className="founder-page-intro-video-placeholder">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      play_circle
                    </span>
                    <span>{intro.videoPlaceholder}</span>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="founder-page-hero">
        <div className="founder-page-hero-bg" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2400&q=80"
            alt=""
          />
        </div>
        <div className="container founder-page-hero-inner">
          <Reveal direction="left">
            <div className="founder-page-hero-copy">
              <span className="eyebrow founder-page-hero-eyebrow">{f.heroEyebrow}</span>
              <h1 className="founder-page-hero-title">{f.heroName}</h1>
              <p className="founder-page-hero-role">{f.heroRole}</p>
              <p className="founder-page-hero-tagline">{f.heroTagline}</p>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="founder-image founder-page-hero-visual founder-page-hero-photo">
              <div className="founder-ring" aria-hidden="true" />
              <img
                className="founder-page-hero-photo-img"
                src={`${process.env.PUBLIC_URL}/images/img.png`}
                alt={f.heroName}
                width={800}
                height={800}
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="founder-page-block founder-page-about">
        <div className="container">
          <Reveal>
            <div className="founder-page-about-grid">
              <div>
                <span className="eyebrow">{f.aboutEyebrow}</span>
                <h2 className="founder-page-section-title">{f.aboutTitle}</h2>
                <ul className="founder-page-taglist">
                  {f.aboutTags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </div>
              <div className="founder-page-about-quote">
                <p className="founder-page-prose">{f.aboutText}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="founder-page-block founder-page-career">
        <div className="founder-page-career-bg" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80"
            alt=""
          />
        </div>
        <div className="container">
          <Reveal>
            <span className="eyebrow founder-page-eyebrow-invert">{f.careerEyebrow}</span>
            <h2 className="founder-page-section-title">{f.careerTitle}</h2>
            <p className="founder-page-section-sub">{f.careerSubtitle}</p>
          </Reveal>
          <div className="founder-page-milestones">
            {f.milestones.map((m, idx) => (
              <Reveal key={m.num} delay={idx * 130} direction={idx % 2 ? 'left' : 'right'}>
                <div className={`founder-milestone-row ${idx % 2 ? 'founder-milestone-row-right' : 'founder-milestone-row-left'}`}>
                  <article className="founder-milestone">
                    <div className="founder-milestone-head">
                      <span className="founder-milestone-num">{m.num}</span>
                      <div>
                        <p className="founder-milestone-org">{m.org}</p>
                        <h3 className="founder-milestone-role">{m.role}</h3>
                      </div>
                    </div>
                    <ul className="founder-milestone-bullets">
                      {m.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </article>
                  <span className="founder-milestone-node" aria-hidden="true" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="founder-page-block founder-page-media">
        <div className="container">
          <Reveal>
            <div className="services-head">
              <span className="eyebrow center">{m.eyebrow}</span>
              <h2 className="founder-page-section-title">{m.title}</h2>
              <p className="founder-page-section-sub">{m.subtitle}</p>
            </div>
          </Reveal>

          <div className="founder-page-media-grid">
            <Reveal direction="left">
              <div className="founder-media-video">
                <div className="founder-media-video-frame">
                  {youtubeEmbedSrc ? (
                    <iframe
                      title={m.title}
                      src={`${youtubeEmbedSrc}?rel=0`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  ) : (
                    <video controls playsInline preload="metadata" src={m.videoSrc} />
                  )}
                </div>
                {m.videoCaption ? <p className="founder-media-video-caption">{m.videoCaption}</p> : null}
              </div>
            </Reveal>

            <Reveal direction="right">
              <div className="founder-media-links">
                <p className="founder-media-links-title">{m.linksTitle}</p>
                <div className="founder-media-link-list">
                  {m.links.map((link) => (
                    <a
                      key={link.href}
                      className="founder-media-link"
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="material-symbols-outlined founder-media-link-icon" aria-hidden="true">
                        {link.icon}
                      </span>
                      <span className="founder-media-link-label">{link.label}</span>
                      <span className="material-symbols-outlined founder-media-link-arrow" aria-hidden="true">
                        north_east
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="founder-page-block founder-page-vision">
        <div className="container">
          <Reveal>
            <span className="eyebrow">{f.visionEyebrow}</span>
            <h2 className="founder-page-section-title">{f.visionTitle}</h2>
            <p className="founder-page-section-sub founder-page-vision-intro">{f.visionIntro}</p>
          </Reveal>
          <div className="founder-page-vision-grid">
            {f.visionItems.map((item, idx) => (
              <Reveal key={item.num}>
                <article className="founder-vision-card">
                  <span className="material-symbols-outlined founder-vision-icon" aria-hidden="true">
                    {visionIcons[idx % visionIcons.length]}
                  </span>
                  <span className="founder-vision-num">{item.num}</span>
                  <h3 className="founder-vision-card-title">{item.title}</h3>
                  <p className="founder-vision-card-text">{item.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="founder-page-block founder-page-philosophy">
        <div className="container">
          <Reveal>
            <blockquote className="founder-page-philosophy-quote">
              <span className="material-symbols-outlined founder-page-philosophy-icon" aria-hidden="true">
                format_quote
              </span>
              <span className="eyebrow founder-page-philosophy-eyebrow founder-page-eyebrow-invert">
                {f.philosophyEyebrow}
              </span>
              <p>{f.philosophyQuote}</p>
              <footer>{f.philosophyAttribution}</footer>
            </blockquote>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default FounderPage;
